import type { CaptureNode } from "@capture/captureClient";
import { useSetRoomCapture } from "@capture/captureQueries";
import { ConnectDeviceButton } from "@capture/ConnectDeviceButton";
import { faPause, faPlay } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Chip,
  ConfirmDialog,
  FieldError,
} from "@scribenote/brian-design-system";
import { formatRelativeTimeMs } from "@utils/format-date";
import { useState } from "react";

import { roomCaptureState } from "./roomCaptureState";

interface RoomCaptureProps {
  roomUuid: string;
  roomName: string;
  nodes: CaptureNode[];
  desiredEnabled: boolean | null;
  desiredRevision: number;
}

type CaptureState = ReturnType<typeof roomCaptureState>;

/** Paused or Capturing is only ever the devices' own report, never the PUT response. */
const CaptureStatus = ({
  capture,
  roomUuid,
  roomName,
  nodes,
}: {
  capture: CaptureState;
  roomUuid: string;
  roomName: string;
  nodes: CaptureNode[];
}) => {
  const testId = `room-capture-status-${roomUuid}`;
  if (capture.failed)
    return (
      <Chip tone="attention" size="sm" data-testid={testId}>
        Control failed
      </Chip>
    );
  if (capture.unavailable && nodes.length === 0)
    return <ConnectDeviceButton roomUuid={roomUuid} roomName={roomName} />;
  if (capture.unavailable && capture.enabled)
    return (
      <span className="flex flex-col" data-testid={testId}>
        <span className="text-xs text-annotation">
          {nodes.some((node) => node.connectorLive)
            ? "Device offline"
            : "Clinic computer offline"}
        </span>
        <span className="font-mono text-xs text-annotation">
          seen{" "}
          {formatRelativeTimeMs(
            Math.max(...nodes.map((node) => Date.parse(node.lastSeen))),
          )}
        </span>
      </span>
    );
  if (capture.unavailable)
    return (
      <span className="text-xs text-annotation" data-testid={testId}>
        Pauses on reconnect
      </span>
    );
  if (capture.pending)
    return (
      <span className="text-xs text-annotation" data-testid={testId}>
        {capture.enabled ? "Resuming" : "Pausing"}
      </span>
    );
  if (capture.applied === false)
    return (
      <Chip tone="declined" size="sm" data-testid={testId}>
        Paused
      </Chip>
    );
  return (
    <Chip tone="recorded" size="sm" data-testid={testId}>
      Capturing
    </Chip>
  );
};

export const RoomCaptureControl = ({
  roomUuid,
  roomName,
  nodes,
  desiredEnabled,
  desiredRevision,
}: RoomCaptureProps) => {
  const mutation = useSetRoomCapture();
  const [confirming, setConfirming] = useState(false);
  const capture = roomCaptureState(
    nodes,
    { enabled: desiredEnabled, revision: desiredRevision },
    roomUuid,
    mutation.data,
  );
  // A failed command retries the same target rather than flipping it.
  const target = capture.failed ? capture.enabled : !capture.enabled;
  const action = target ? "Resume" : "Pause";
  const applying = capture.pending && !capture.unavailable && !capture.failed;
  const inFlight = capture.enabled ? "Resume" : "Pause";

  return (
    <span className="flex flex-wrap items-center gap-3">
      <span className="w-40">
        <CaptureStatus
          capture={capture}
          roomUuid={roomUuid}
          roomName={roomName}
          nodes={nodes}
        />
      </span>
      <Button
        variant="outline"
        size="sm"
        busy={mutation.isPending || applying}
        onClick={() => setConfirming(true)}
        aria-label={`${applying ? inFlight : action} capture in ${roomName}`}
        data-testid={`room-capture-${target ? "resume" : "pause"}-${roomUuid}`}
      >
        <FontAwesomeIcon icon={target ? faPlay : faPause} aria-hidden />
        {applying ? inFlight : action}
      </Button>
      <ConfirmDialog
        open={confirming}
        title={`${action} capture in ${roomName}?`}
        confirmLabel={action}
        destructive={!target}
        busy={mutation.isPending}
        onCancel={() => {
          mutation.reset();
          setConfirming(false);
        }}
        onConfirm={() =>
          mutation.mutate(
            { roomUuid, enabled: target },
            { onSuccess: () => setConfirming(false) },
          )
        }
        confirmTestId={`room-capture-confirm-${roomUuid}`}
        cancelTestId={`room-capture-cancel-${roomUuid}`}
      >
        <p className="text-sm text-ink-body">
          {target
            ? "Audio and camera capture will restart at the room device."
            : "Audio and camera capture will stop at the room device. Nothing new is recorded until capture is resumed."}
          {capture.unavailable &&
            " No device is connected, so this applies when one reconnects."}
        </p>
        {mutation.isError && (
          <FieldError className="mt-2">
            Couldn&rsquo;t update room capture. Try again.
          </FieldError>
        )}
      </ConfirmDialog>
    </span>
  );
};
